import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosConfig, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { User } from 'swagger/models';
import { useSetAtom } from 'jotai';
import { provisioningErrorHandledAtom, failedLoginAtom, failedLoginMessageAtom } from 'auth/userStore';
import { API_CLIENT_HEADERS } from 'api/OpenApiConfiguration';
import { isEmpty, isNull } from 'lodash';
import AxiosConfig from 'api/AxiosConfig';
import { NextRouter, useRouter } from 'next/router';
import { removeNotTrackingParams } from 'helpers/urlQuery';
import useLocalStorage from 'hooks/useLocalStorageSafe';
import { useAuth, useUser } from '@clerk/nextjs';
import { useEffect, useState } from 'react';
import { shouldBypassClerk } from 'helpers/clerk';
import { defaultPic } from 'helpers';

const FIVE_MIN = 5 * 60 * 1000;

export const buildGetPersonalProfileRequestConfig = (router: NextRouter): AxiosRequestConfig => {
  const config: AxiosRequestConfig = { headers: API_CLIENT_HEADERS };
  const analyticsParams = removeNotTrackingParams(router.query);

  if (router.isReady && !isEmpty(analyticsParams)) {
    config.params = analyticsParams;
  }

  return config;
};

const axios = axiosConfig.create(AxiosConfig);

interface IDomesticMeSession {
  galactica_session: string;
}

interface MeResponseError {
  error?: string;
}

export const useUserDetails = () => {
  const setFailedLogin = useSetAtom(failedLoginAtom);
  const setFailedLoginMessage = useSetAtom(failedLoginMessageAtom);
  const setProvisioningErrorHandled = useSetAtom(provisioningErrorHandledAtom);
  const [userKey, setUserKey] = useState('guest');
  const router = useRouter();

  const isClerkEnabled = !shouldBypassClerk();

  const { isLoaded: clerkLoaded, isSignedIn, userId } = useAuth();
  const { user: clerkUser } = useUser();

  // legacy: run once router ready
  // clerk : run when router ready, flag true, Clerk loaded, and signed‑in
  const enabled = router.isReady && (!isClerkEnabled || (clerkLoaded && isSignedIn));

  useEffect(() => {
    if (userId) {
      setUserKey(userId);
    }
  }, [userId]);

  // As soon as Clerk confirms sign-in, set the nav cookie from Clerk's user
  // data so the auth nav renders immediately (before /me returns).
  useEffect(() => {
    if (isClerkEnabled && clerkLoaded && isSignedIn && clerkUser) {
      const existing = document.cookie.match(/bs_nav=([^;]+)/);
      if (!existing) {
        const name = clerkUser.firstName || '';
        const avatar = defaultPic(name);
        const value = encodeURIComponent(`${name}|${avatar}`);
        document.cookie = `bs_nav=${value}; path=/; max-age=2592000; SameSite=Lax`;
        const el = document.documentElement;
        el.dataset.bsNav = name;
        el.style.setProperty('--bs-nav-avatar', `url('${avatar}')`);
      }
    }
  }, [isClerkEnabled, clerkLoaded, isSignedIn, clerkUser]);

  // Clear stale nav cookie + pre-hydration hint when Clerk confirms sign-out
  useEffect(() => {
    if (isClerkEnabled && clerkLoaded && !isSignedIn) {
      document.cookie = 'bs_nav=; path=/; max-age=0';
      const el = document.documentElement;
      delete el.dataset.bsNav;
      el.style.removeProperty('--bs-nav-avatar');
    }
  }, [isClerkEnabled, clerkLoaded, isSignedIn]);

  const {
    data: { data: userDetails = {} } = {},
    isLoading,
    ...rest
  } = useQuery<AxiosResponse<User>>(
    ['userDetails', userKey],
    () => axios.get('/domestic/v1/me', buildGetPersonalProfileRequestConfig(router)),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      staleTime: FIVE_MIN,
      onError: (err: AxiosError<MeResponseError>) => {
        if (err?.response?.status === 422 && err?.response?.data) {
          setFailedLoginMessage(err.response.data.error);
        }
        setFailedLogin(true);
      },
      onSuccess: async (payload) => {
        setProvisioningErrorHandled(false); // reset for next session
        setFailedLoginMessage('');
        setFailedLogin(false);

        // Set nav hint cookie so the inline script in _document.tsx can show
        // the user's avatar before React hydrates (prevents auth flash)
        const name = payload.data.first_name || '';
        const picUrl = payload.data.picture?.url || '';
        const avatar = picUrl.startsWith('http') && !picUrl.includes('default_profile_pic') ? picUrl : defaultPic(name);
        document.cookie = `bs_nav=${encodeURIComponent(`${name}|${avatar}`)}; path=/; max-age=2592000; SameSite=Lax`;

        if (payload.data.firebase_token) {
          const result = await import('stacks/messaging/Firebase');
          result.initFirebase();
          import('auth/firebase').then((e) => e.authFirebase(payload.data.firebase_token));
        }
      },
    }
  );

  const isAuthenticated = !isEmpty(userDetails);
  const isAuthResolved = !isClerkEnabled || (clerkLoaded && !isSignedIn) || isAuthenticated;

  const [galacticaSession, setGalacticaSession] = useLocalStorage('galactica_session', '');
  const [galacticaSessionUpdatedAt, setGalacticaSessionUpdatedAt] = useLocalStorage('galactica_session_updated_at', 0);

  const isLastSessionFetchNotRelevant =
    isNull(galacticaSessionUpdatedAt) || galacticaSessionUpdatedAt < new Date().getTime() - FIVE_MIN;
  if (!isAuthenticated && !isEmpty(galacticaSession) && isLastSessionFetchNotRelevant) {
    setGalacticaSession('');
  }

  useQuery<AxiosResponse<IDomesticMeSession>>(
    ['galacticaSessionOnEmpty', isAuthenticated, galacticaSession, isLastSessionFetchNotRelevant],
    () =>
      axios.post(`/domestic/v2/me/session`, undefined, {
        headers: {
          ...API_CLIENT_HEADERS,
        },
      }),
    {
      enabled: isEmpty(galacticaSession) && isAuthenticated && isLastSessionFetchNotRelevant,
      staleTime: FIVE_MIN,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      onError: () => {
        setGalacticaSession('');
        setGalacticaSessionUpdatedAt(new Date().getTime());
      },
      onSuccess: async (payload) => {
        setGalacticaSession(payload.data.galactica_session);
        setGalacticaSessionUpdatedAt(new Date().getTime());
      },
    }
  );

  return {
    userId: userDetails?.id,
    userDetails,
    isAuthenticated,
    isAuthResolved,
    isLoading,
    ...rest,
  };
};

export const useUpdateUserMutation = (invalidate = true) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: User & { terms_of_service?: boolean; tcpa_accepted?: boolean }) =>
      axios.patch('/domestic/v1/me', body, {
        headers: API_CLIENT_HEADERS,
      }),
    {
      onSuccess: async () => {
        if (invalidate) await queryClient.invalidateQueries('userDetails');
      },
    }
  );
};
